// Finar rev-B 3 — KpiGrid formüllü hücre hover/odak kartı durumu (KpiGrid ≤600 satır kuralı için
// ayrı dosya). Olaylar tablo düzeyinde yakalanır (delegasyon): türetilmiş gösterge değer hücresi
// `data-formula-cell="satır:kolon"` taşır (KpiGridRow). İmleç ya da klavye odağı hücrede ~300 ms
// kalınca kart açılır; hücreden çıkınca / Escape'te / bir popover ya da editör açıkken kapanır.
// Dokunmatik işaretçi kartı açmaz (dokunuş zaten FormulaPopover'ı açar). Görünürken hücreye
// `aria-describedby` bağlanır, kapanınca kaldırılır.

import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import type { KpiResolvedGrid } from "@mahir/shared";
import { formulaHoverContent } from "../../lib/kpi-formula-hover";
import {
  formulaInputsFor,
  type CellPos,
  type GridColumn,
  type GridRow,
} from "../../lib/kpi-grid-nav";
import { FORMULA_HOVER_ID, FormulaHoverCard } from "./FormulaHoverCard";

export const FORMULA_HOVER_DELAY_MS = 300;

/** `data-formula-cell` değeri ("3:1") → konum; geçersizse null. */
export function parseFormulaCellAttr(value: string | null | undefined): CellPos | null {
  if (!value) return null;
  const m = /^(\d+):(\d+)$/.exec(value);
  return m ? { row: Number(m[1]), col: Number(m[2]) } : null;
}

function formulaCellOf(target: EventTarget | null): HTMLElement | null {
  const el = target instanceof Element ? target.closest("[data-formula-cell]") : null;
  return el instanceof HTMLElement ? el : null;
}

export function useFormulaHover(opts: {
  rows: GridRow[];
  columns: GridColumn[];
  grid: KpiResolvedGrid;
  /** portal görünümü (KpiGrid `clientView`) — verilirse etiket + maske */
  clientView: unknown;
  cellRefs: MutableRefObject<Map<string, HTMLTableCellElement>>;
  /** formül/raporlanan/not popover'ı ya da hücre editörü açık → kart gösterilmez */
  suppressed: boolean;
}): {
  handlers: {
    onPointerOver: (e: React.PointerEvent) => void;
    onPointerOut: (e: React.PointerEvent) => void;
    onFocus: (e: React.FocusEvent) => void;
    onBlur: (e: React.FocusEvent) => void;
  };
  card: JSX.Element | null;
} {
  const { rows, columns, grid, cellRefs, suppressed } = opts;
  const client = !!opts.clientView;
  const [shown, setShown] = useState<CellPos | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<string | null>(null);

  const clear = (): void => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    pending.current = null;
  };
  const hide = useCallback((): void => {
    clear();
    setShown(null);
  }, []);
  const schedule = (el: HTMLElement): void => {
    const key = el.dataset.formulaCell ?? "";
    const pos = parseFormulaCellAttr(key);
    if (!pos || pending.current === key) return;
    if (shown && `${shown.row}:${shown.col}` === key) return;
    clear();
    setShown(null);
    pending.current = key;
    timer.current = setTimeout(() => {
      pending.current = null;
      setShown(pos);
    }, FORMULA_HOVER_DELAY_MS);
  };

  useEffect(() => () => clear(), []);
  useEffect(() => {
    if (suppressed) hide();
  }, [suppressed, hide]);
  // satır/kolon dizisi değişince (bant kapandı, arama, yenileme) konum başka göstergeyi gösterebilir
  useEffect(() => hide(), [rows, columns, hide]);

  // görünürken: Escape kapatır + hücreye aria-describedby
  const key = shown ? `${shown.row}:${shown.col}` : null;
  useEffect(() => {
    if (!key) return;
    const el = cellRefs.current.get(key);
    el?.setAttribute("aria-describedby", FORMULA_HOVER_ID);
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") hide();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      el?.removeAttribute("aria-describedby");
    };
  }, [key, cellRefs, hide]);

  const handlers = {
    onPointerOver: (e: React.PointerEvent): void => {
      if (e.pointerType === "touch" || suppressed) return;
      const el = formulaCellOf(e.target);
      if (el) schedule(el);
    },
    onPointerOut: (e: React.PointerEvent): void => {
      const from = formulaCellOf(e.target);
      if (!from) return;
      if (formulaCellOf(e.relatedTarget) === from) return; // hücre içindeki rozete geçiş
      hide();
    },
    onFocus: (e: React.FocusEvent): void => {
      const el = formulaCellOf(e.target);
      if (el && e.target === el && !suppressed) schedule(el);
      else if (!el) hide();
    },
    onBlur: (e: React.FocusEvent): void => {
      const from = formulaCellOf(e.target);
      if (from && formulaCellOf(e.relatedTarget) !== from) hide();
    },
  };

  const row = shown ? rows[shown.row] : undefined;
  const col = shown ? columns[shown.col] : undefined;
  let card: JSX.Element | null = null;
  if (shown && row && col && !suppressed && row.indicator.isDerived && !row.isBand) {
    const inputs = client ? formulaInputsFor(row.indicator, grid, col) : null;
    const content = formulaHoverContent(
      row.indicator,
      inputs ? { labelByCode: inputs.labels, hidden: inputs.hidden } : undefined,
    );
    const anchorKey = `${shown.row}:${shown.col}`;
    card = (
      <FormulaHoverCard
        key={anchorKey}
        content={content}
        getAnchor={() => cellRefs.current.get(anchorKey) ?? null}
      />
    );
  }
  return { handlers, card };
}
